import * as React from "react";
import { $, merge } from "glamor";
import { ICellEditorParams } from "ag-grid-community/dist/lib/interfaces/iCellEditor";
import { isValidNumber } from "lib/number";
import me from "models/me";
import { getAvailableInventory } from "containers/legacy-cart/helpers";
import { GridShipmentRowCallbackParams } from "components/shipment-review/types";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IProps extends GridShipmentRowCallbackParams<ICellEditorParams> {
    editable?: boolean;
    isZeroValueAllowed?: boolean;
    [key: string]: any;
}

enum EDIT_MODES {
    REPLACE,
    APPEND,
}

export class SizeCellEditor extends React.PureComponent<IProps, any> {
    private inputRef;
    private takeDefaultValue = false;
    private defaultValue = "";

    private styles = merge(
        {
            fontFamily: "Lato !important",
        },
        $(" .price", {
            fontSize: "10px",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.2",
            letterSpacing: "normal",
            textAlign: "right",
            color: "#8e8e8e",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: "10px",
            marginRight: "11px",
        }),
        $(" input[type=number]::-webkit-inner-spin-button", {
            WebkitAppearance: "none",
            margin: "0",
        }),
        $(" .quantity", {
            fontSize: "16px",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.13",
            letterSpacing: "0.21px",
            textAlign: "right",
            color: "#585858",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            border: "none",
        }),
        $(" .quantity:hover", {
            cursor: "pointer",
        }),
        $(" input", {
            height: "27px",
            textAlign: "center",
            width: "90% !important",
            marginLeft: "auto",
            marginRight: "auto",
            position: "relative",
            display: "block",
            marginBottom: "3px",
        })
    );

    constructor(props: IProps) {
        super(props);
        const { data, colDef } = props;
        const { currency } = data;
        const sizeData = data.sizes[colDef.colId];
        let editMode = EDIT_MODES.APPEND;
        this.defaultValue = String(sizeData.quantity);
        let quantity = isValidNumber(this.defaultValue) ? this.defaultValue : "";
        const price = sizeData?.pricing?.wholesalePrice ?? 0;
        const formatter = new Intl.NumberFormat("en-US", {
            style: "decimal",
            minimumFractionDigits: 2,
        });
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            quantity = "";
        } else if (props.charPress && isValidNumber(props.charPress)) {
            quantity = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        }
        this.state = {
            quantity,
            editMode,
            price,
            formatter,
            currency,
            sizeData,
            prebook: data.item.prebook,
        };
    }

    componentDidMount() {
        this.focus();
    }

    // Check if cell should select all when starts editing
    selectAllOnStart() {
        const { charPress, eventKey } = this.props;
        if (eventKey === KEYBOARD_KEYS.ENTER) {
            // On ENTER press
            return true;
        } else if (!eventKey && !charPress) {
            // On mouse double click or focused on tab
            return true;
        }

        return false;
    }

    focus() {
        const startIndex = !this.selectAllOnStart() ? this.state.quantity.toString().length : 0;
        const endIndex = isValidNumber(this.state.quantity) ? this.state.quantity.toString().length : 0;
        if (this.inputRef) {
            (this.inputRef as any).focus();
            (this.inputRef as any).setSelectionRange(startIndex, endIndex);
        }
    }

    getValue() {
        const value = this.takeDefaultValue ? this.defaultValue : this.state.quantity;
        const num = Number(value);
        return isNaN(num) || value === "" ? value : num;
    }

    handleChange = (event) => {
        if (isValidNumber(event.target.value) || !event.target.value) {
            this.setState({ quantity: event.target.value });
        }
    };

    setRef = (ref) => {
        this.inputRef = ref;
    };

    render() {
        const { context, data } = this.props;
        const { quantity, sizeData, prebook } = this.state;
        const inventory = getAvailableInventory(sizeData, {
            useDisplayInventory: me.hasPermission("display_inventory"),
            quantity,
            prebook,
        });
        return (
            <div {...this.styles} className="size-cell-editor">
                <input
                    ref={this.setRef}
                    type="text"
                    value={quantity}
                    onChange={this.handleChange}
                    onKeyDown={this.handleKeyDown}
                />
                {!data?.hideInventory && <div className="inventory">{inventory}</div>}
                {!context?.suppressPrice && (
                    <div className="price">
                        {this.state.currency?.wholesale?.symbol}
                        {this.state.formatter.format(this.state.price)}
                    </div>
                )}
            </div>
        );
    }

    private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { editMode } = this.state;
        const { api } = this.props;
        const currentCell = api.getEditingCells()[0];

        switch (e.key) {
            case KEYBOARD_KEYS.UP:
            case KEYBOARD_KEYS.DOWN:
                if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) {
                    let value = Number(this.state.quantity);
                    if (!isNaN(value)) {
                        value = e.key === KEYBOARD_KEYS.UP ? value + 1 : value - 1;
                        this.setState({ value });
                        process.nextTick(() => {
                            (this.inputRef as any).setSelectionRange(0, value.toString().length);
                        });
                        e.stopPropagation();
                    }
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case KEYBOARD_KEYS.LEFT:
            case KEYBOARD_KEYS.RIGHT:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented && editMode === EDIT_MODES.REPLACE) {
                    this.props.stopEditing();
                    this.navigateToCell(e, currentCell);
                    e.preventDefault();
                }
                break;
            case KEYBOARD_KEYS.ENTER:
                this.props.stopEditing();
                this.navigateToCell(e, currentCell);
                break;
            case KEYBOARD_KEYS.ESCAPE:
                this.takeDefaultValue = true;
                this.props.stopEditing();
                break;
            default:
        }
    };

    private navigateToCell = (e: React.KeyboardEvent<HTMLInputElement>, currentCell) => {
        const { api } = this.props;
        if (currentCell) {
            api.setFocusedCell(currentCell.rowIndex, currentCell.column.getColId());
        }

        const lastRow = api.getLastDisplayedRow();

        const focusedCell = api.getFocusedCell();
        api.clearRangeSelection();
        switch (e.key) {
            case KEYBOARD_KEYS.RIGHT:
                api.tabToNextCell();
                break;
            case KEYBOARD_KEYS.LEFT:
                api.tabToPreviousCell();
                break;
            case KEYBOARD_KEYS.UP:
                if (focusedCell.rowIndex > 0) {
                    api.setFocusedCell(Math.max(0, focusedCell.rowIndex - 1), focusedCell.column.getColId());
                }
                break;
            case KEYBOARD_KEYS.DOWN:
            case KEYBOARD_KEYS.ENTER:
                api.setFocusedCell(Math.min(focusedCell.rowIndex + 1, lastRow), focusedCell.column.getColId(), null);
                break;
            default:
        }
    };
}
